import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie } from "lucide-react";

const KEY = "mdp_cookie_consent";
const SIX_MONTHS = 1000 * 60 * 60 * 24 * 182;

export const readConsent = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!raw || Date.now() - raw.at > SIX_MONTHS) return null;
    return raw;
  } catch {
    return null;
  }
};

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [custom, setCustom] = useState(false);
  const [audience, setAudience] = useState(false);

  useEffect(() => {
    if (!readConsent()) setVisible(true);
    const open = () => { setCustom(false); setVisible(true); };
    window.addEventListener("mdp:open-cookies", open);
    return () => window.removeEventListener("mdp:open-cookies", open);
  }, []);

  const save = (prefs) => {
    localStorage.setItem(KEY, JSON.stringify({ ...prefs, at: Date.now() }));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.section
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-title"
          className="fixed inset-x-4 bottom-4 z-[90] mx-auto max-w-3xl rounded-xl border border-line-dark bg-night p-6 text-white shadow-2xl sm:inset-x-6 sm:p-7"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4 }}
          data-testid="cookie-banner"
        >
          <div className="flex gap-4">
            <Cookie className="mt-1 h-6 w-6 shrink-0 text-gold" aria-hidden="true" />
            <div className="flex-1">
              <h2 id="cookie-title" className="font-heading text-lg font-bold">Gestion des cookies</h2>
              <p className="mt-2 text-sm leading-relaxed text-white/75">
                Ce site utilise uniquement des cookies strictement nécessaires à son fonctionnement et à la mémorisation de votre choix (6 mois).
                Aucun cookie non essentiel n'est déposé sans votre consentement.{" "}
                <Link to="/politique-de-confidentialite" className="underline decoration-gold/60 underline-offset-4 hover:text-gold">En savoir plus</Link>
              </p>
              {custom && (
                <div className="mt-4 space-y-3 rounded-lg border border-white/10 p-4" data-testid="cookie-custom-panel">
                  <label className="flex items-center justify-between gap-4 text-sm">
                    <span><strong>Cookies nécessaires</strong><br /><span className="text-white/60">Toujours actifs — fonctionnement et sécurité du site.</span></span>
                    <input type="checkbox" checked disabled className="h-5 w-5 accent-gold" aria-label="Cookies nécessaires (toujours actifs)" />
                  </label>
                  <label className="flex items-center justify-between gap-4 text-sm">
                    <span><strong>Mesure d'audience</strong><br /><span className="text-white/60">Statistiques anonymisées de fréquentation (non activé à ce jour).</span></span>
                    <input type="checkbox" checked={audience} onChange={(e) => setAudience(e.target.checked)} className="h-5 w-5 accent-gold" data-testid="cookie-audience-toggle" />
                  </label>
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-3">
                <button type="button" onClick={() => save({ necessary: true, audience: true })} className="btn-gold !px-5 !py-2.5" data-testid="cookie-accept-btn">Accepter</button>
                <button type="button" onClick={() => save({ necessary: true, audience: false })} className="btn-outline-light !px-5 !py-2.5" data-testid="cookie-refuse-btn">Refuser</button>
                {custom ? (
                  <button type="button" onClick={() => save({ necessary: true, audience })} className="btn-outline-light !px-5 !py-2.5" data-testid="cookie-save-btn">Enregistrer mes choix</button>
                ) : (
                  <button type="button" onClick={() => setCustom(true)} className="btn-outline-light !px-5 !py-2.5" data-testid="cookie-customize-btn">Personnaliser</button>
                )}
              </div>
            </div>
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  );
};
